import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ChevronLeft,
  Brain,
  Zap,
  Compass,
  Leaf,
} from "lucide-react";

const TransitionScreen = ({
  isOpen,
  onContinue,
  onBack,
  userName,
  userPhoto,
  personality,
}) => {
  if (!isOpen) return null;

  // Get first name from full name
  const firstName = userName?.split(" ")[0] || "Friend";

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  // Get personalized content based on personality
  const getPersonalizedContent = (personalityType) => {
    const contentMap = {
      leader: {
        greeting: `Ready to get started, ${firstName}? ⚡`,
        mainMessage:
          "Life doesn't wait. When you need Biblical wisdom to guide a decision, you need it now—not after hours of research.",
        benefit:
          "BibeCheck gets you there fast. Ask. Get direction. Move forward with confidence.",
        subscription:
          "Your subscription means unlimited guidance when it matters most.",
        cta: "Start Now",
        Icon: Zap,
        gradient: "from-orange-500 via-red-500 to-pink-500",
        iconBg: "from-orange-100 to-red-100",
        iconColor: "text-orange-600",
      },
      thinker: {
        greeting: `Excited to dive in with you, ${firstName}! 🧠`,
        mainMessage:
          "Here's the thing: the Bible is one big, connected story. But most tools make you study it in tiny, disconnected pieces.",
        benefit:
          "BibeCheck lets you discover how it all fits together. Explore context visually. Analyze connections. Follow every thread as far as you want.",
        subscription:
          "Your subscription unlocks unlimited exploration—so you can keep discovering.",
        cta: "Choose Your Plan",
        Icon: Brain,
        gradient: "from-purple-500 via-blue-500 to-indigo-500",
        iconBg: "from-purple-100 to-blue-100",
        iconColor: "text-purple-600",
      },
      promoter: {
        greeting: `This is going to be amazing, ${firstName}! ✨`,
        mainMessage:
          "Imagine enjoying the Bible—actually having fun exploring it. Not confused. Not bored. Genuinely excited about what you find.",
        benefit:
          "BibeCheck makes that happen. Ask anything. Tap to go deeper. Discover insights you'll actually want to share.",
        subscription:
          "Your subscription keeps the fun going—unlimited questions, unlimited discoveries.",
        cta: "Let's Get Started! ✨",
        Icon: Compass,
        gradient: "from-pink-500 via-purple-500 to-blue-500",
        iconBg: "from-pink-100 to-purple-100",
        iconColor: "text-pink-600",
      },
      nurturer: {
        greeting: `So glad you're here, ${firstName} 💙`,
        mainMessage:
          "The Bible can feel overwhelming—like there's so much you \"should\" know but don't.",
        benefit:
          "BibeCheck is here to help you find your way. Go at your own pace. No judgment. Just gentle guidance whenever you're ready.",
        subscription:
          "Your subscription means you can explore without feeling lost—ask questions anytime, get clear answers that help you understand.",
        cta: "Begin Your Journey",
        Icon: Leaf,
        gradient: "from-green-500 via-teal-500 to-emerald-500",
        iconBg: "from-green-100 to-teal-100",
        iconColor: "text-green-600",
      },
    };
    return contentMap[personalityType] || contentMap.nurturer;
  };

  const content = getPersonalizedContent(personality);

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black bg-opacity-60 p-4 pt-4 overflow-y-auto">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden my-8">
        {/* Decorative gradient background - personality-based */}
        <div
          className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${content.gradient}`}
        />

        {/* Back button */}
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 z-10 text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1 text-sm">
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
        )}

        {/* Content */}
        <div className="p-8 md:p-12 pt-8">
          {/* User Photo/Initials */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center mb-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}>
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt={userName || "User"}
                  className={`w-24 h-24 rounded-full object-cover shadow-lg ring-4 ${
                    personality === "leader"
                      ? "ring-orange-200"
                      : personality === "thinker"
                      ? "ring-purple-200"
                      : personality === "promoter"
                      ? "ring-pink-200"
                      : "ring-green-200"
                  }`}
                />
              ) : (
                <div
                  className={`w-24 h-24 rounded-full bg-gradient-to-br ${content.gradient} flex items-center justify-center shadow-lg`}>
                  <span className="text-white text-3xl font-bold">
                    {getInitials(userName)}
                  </span>
                </div>
              )}
            </motion.div>
          </motion.div>

          {/* Personal Greeting */}
          <motion.p
            variants={itemVariants}
            className="text-2xl text-gray-900 font-semibold mb-6 text-center">
            {content.greeting}
          </motion.p>

          {/* Main Message */}
          <div className="space-y-6 text-gray-700 leading-relaxed text-lg mb-8">
            <motion.p variants={itemVariants} className="text-center">
              {content.mainMessage}
            </motion.p>

            <motion.p variants={itemVariants} className="text-center">
              {content.benefit}
            </motion.p>

            <motion.p
              variants={itemVariants}
              className="text-center text-gray-600">
              {content.subscription}
            </motion.p>
          </div>

          {/* CTA Button - personality-based gradient */}
          <motion.button
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onContinue}
            className={`w-full bg-gradient-to-r ${content.gradient} text-white font-semibold text-lg py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 group`}>
            <span className="text-center">{content.cta}</span>
            <ArrowRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default TransitionScreen;
